<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { onMounted, provide, ref, type Ref } from 'vue';
import { onBeforeRouteUpdate, useRoute } from 'vue-router';
import PlanungsgeschaeftEdit from '@/components/PlanungsgeschaeftEdit.vue';
import { usePlanungsgeschaeftAccessControl } from '@/composables/usePlanungsgeschaeftAccessControl';

const route = useRoute();
const geschaeft: Ref<Planungsgeschaeft | null> = ref(null);
const loading: Ref<boolean> = ref(false);
const saving: Ref<boolean> = ref(false);
const planungsgeschaeftAccessControl = usePlanungsgeschaeftAccessControl(geschaeft);

// provide properties that we need within the hierarchy so that we do not have to
// pass them all the way down as params:
provide('saveGeschaeft', saveGeschaeft); // a function to manually execute the saving of a Geschaeft in childs
provide('saving', saving); // indicates if the Geschaeft is saving throuogh this hierarchy
provide('readonly', planungsgeschaeftAccessControl.readonly); // indicates if the Geschaeft is saving throuogh this hierarchy

onMounted(() => {
    loadGeschaeft(Number(route.params.id));
    // acceptInvitations();
});
onBeforeRouteUpdate((to) => loadGeschaeft(Number(to.params.id)));

async function loadGeschaeft(id: number) {
    if (geschaeft.value && Number(route.params.id) === Number(id)) {
        return;
    }
    loading.value = true;
    try {
        geschaeft.value = null;
        geschaeft.value = await Planungsgeschaeft.loadById(Number(id));
    } catch (e) {
        // TODO: error handling
        console.error(e);
    } finally {
        loading.value = false;
    }
}

/**
 * Set the accepted date on all open invitations for this user and this PPA
 *
 * @return     {Promise}  Returns a promise which resolves with void
 */
// async function acceptInvitations(): Promise<void> {
//     await api().post('/Planungsgeschaeft/acceptInvitations', {
//         id: route.params.id
//     });
// }

/**
 * Called when child components modified the Geschaeft. We save it here whenever necessary.
 */
async function onGeschaeftChanged() {
    await saveGeschaeft();
}

async function saveGeschaeft() {
    if (planungsgeschaeftAccessControl.readonly.value) {
        // do not save in readonly case
        return;
    }
    if (geschaeft.value?.isDirty()) {
        saving.value = true;
        try {
            if (geschaeft.value?.isDirty()) {
                await geschaeft.value.save();
            }
            await planungsgeschaeftAccessControl.reevaluate();
        } catch (e) {
            //pass
            console.error(e);
        } finally {
            saving.value = false;
        }
    }
}
</script>

<template>
    <div v-if="loading" class="flex justify-content-center mt-2rem">
        <ktg-loader alt="loading"></ktg-loader>
    </div>
    <div v-else-if="geschaeft !== null">
        <div class="ktg-box-margin-left">
            <RouterLink :to="{ name: 'planungsgeschaefte-my-geschaefte' }">
                <ktg-button appearance="tertiary" size="small" leading-icon="arrow-left"> {{ $t('back') }}</ktg-button>
            </RouterLink>
        </div>

        <PlanungsgeschaeftEdit
            class="mb-5rem"
            :geschaeft="geschaeft"
            :saving="saving"
            @geschaeft-changed="onGeschaeftChanged"
        ></PlanungsgeschaeftEdit>
    </div>
    <div v-else class="my-5rem">
        <ktg-clues appearance="danger">
            <ktg-clue>
                <ktg-clue-title icon="warning">{{ $t('record_not_found') }}</ktg-clue-title>
            </ktg-clue>
        </ktg-clues>
    </div>
</template>
